import React, { useEffect, useState } from 'react';
import { faSignOut } from '@fortawesome/pro-solid-svg-icons/faSignOut';
import { compose } from 'redux';
import { withRouter } from 'react-router';
import Placeholder from '../Placeholder';
import withFiltering from '../../hocs/withFiltering';
import { apiFetch, pluralize, titleize } from '../../utils';
import {
  customers as customersDuck,
  fields as fieldsDuck,
  statuses as statusesDuck,
  modals,
  schoolYears as schoolYearsDuck
} from '../../ducks';
import './LeadSourceReport.scss';
import './styles/CrossApplicantsReport.scss';
import AdmissionsReportNav from './AdmissionsReportNav';
import ContactList from '../ContactList/ContactList';
import { defaultColumn } from '../ContactList/ColumnDefinitions';
import SchoolYearSelect from '../SchoolYearSelect';
import withSchoolYear from '../../hocs/withSchoolYear';
import { duckify, currentCustomer, dependentDuck, filterDuck, singleDuck } from '../../utils/duckify';
import Filters from '../Filters/Filters';
import BulkAction from '../ContactList/BulkActions/BulkAction';
import BulkExportAction from '../ContactList/BulkActions/BulkExportAction';
import BulkActions from '../ContactList/BulkActions/BulkActions';
import FA from '../../utils/FA';

const ProgressBar = ({ percent }) => (
  <div className='progress progress-success'>
    <div className='progress-bar bg-success' style={{ width: `${percent}%` }} />
  </div>
);

const percent = (count, total) => (total > 0 ? Math.round((count * 100) / total) : 0);

const progressCell = (count, total) => (
  <td className='cross-app-progress'><ProgressBar percent={percent(count, total)} /></td>
);

const percentCell = (count, total) => (
  <td className='cross-app-percent'>{`${percent(count, total)}%`}</td>
);

const HistoricalSchool = ({ school, totalHistories }) => {
  const schoolColumns = [
    defaultColumn({
      header: 'Year',
      value: 'name',
      subRowValue: ''
    }),
    defaultColumn({
      header: 'Count',
      columnClassNames: 'cross-app-count',
      value: 'count'
    }),
    defaultColumn({
      header: 'Percentage',
      value: (year) => percentCell(year.count, totalHistories[year.name])
    }),
    defaultColumn({
      header: '',
      value: (year) => progressCell(year.count, totalHistories[year.name])
    })
  ];

  return (
    <div key={school.id} className='cross-app-historical-school'>
      <h4>{school.name}</h4>
      <ContactList
        className='stripe-balance-transactions'
        columns={schoolColumns}
        contacts={school.years}
      />
    </div>
  );
};
const CrossApplicantsReport = (
  {
    // withSchoolYear
    onSchoolYearChange,
    schoolYearId,

    // mapDucksToProps
    schoolYears,
    fields,
    applicantStatuses,

    // withRouter
    location,

    // mapDispatchToProps
    showModal,

    // with filtering
    filters,
    setFilters
  }
) => {
  const [ready, setReady] = useState(false);
  const [report, setReport] = useState(
    { total_for_year: 0, schools_for_year: [], total_histories: {}, school_histories: [] }
  );

  const searchParams = new URLSearchParams(location.search);
  const isArchived = year => year.archived
  const isSelected = year => year.id == schoolYearId
  const dropdownSchoolYears = schoolYears.filter(year => !isArchived(year) || isSelected(year))

  useEffect(() => {
    setReady(false);
    apiFetch.get({}, `reporting/cross_applicants_report?${searchParams}`)
      .then(json => setReport(json.cross_applicants_report))
      .catch(() => showModal('UnexpectedError'))
      .finally(() => setReady(true));
  }, [location.search]);

  const downloadMessage = (
    <React.Fragment>
      Your file is being prepared. You can safely close this modal and continue working. The report will automatically
      download when completed and will also be available on the
      {' '}
      <a href='/setup/user_jobs' target='_blank'>User Jobs page.</a>
    </React.Fragment>
  );

  const currentTotal = report.total_for_year;
  const currentSchools = report.schools_for_year;
  const totalHistories = report.total_histories;
  const schoolHistories = report.school_histories;

  const currentSchoolColumns = [
    defaultColumn({
      header: 'School Name',
      value: 'name',
      subRowValue: ''
    }),
    defaultColumn({
      header: 'Count',
      columnClassNames: 'cross-app-count',
      value: 'count'
    }),
    defaultColumn({
      header: 'Percentage',
      value: (school) => percentCell(school.count, currentTotal)
    }),
    defaultColumn({
      header: '',
      value: (school) => progressCell(school.count, currentTotal)
    })
  ];

  return (
    <React.Fragment>
      <h2>Explore Applicants Report</h2>

      <AdmissionsReportNav view='cross_applicants' />

      <BulkActions>
        <BulkAction title='Export' icon={faSignOut}>
          <BulkExportAction
            title='Download as PDF'
            type='CrossApplicantsReportExportJob'
            location={location}
            url='/api/reporting/cross_applicants_report/export.pdf'
            message={downloadMessage}
          />
        </BulkAction>
      </BulkActions>

      <Placeholder.loading
        ready={ready}
        content={() => (
          <div className='cross-app-reports'>
            <div className='cross-app-current'>
              <div className='cross-app-filters'>
                <div className='cross-app-year'>
                  <FA name='camera' postNbsp />
                  SHARED APPLICANT SNAPSHOT FOR
                  &nbsp;
                  {
                    schoolYears.ready && (
                      <SchoolYearSelect
                        onSelect={(year) => onSchoolYearChange(year)}
                        schoolYearId={schoolYearId}
                        schoolYears={dropdownSchoolYears}
                      />
                    )
                  }
                </div>
                <Filters
                  fields={fields.concat([
                    {
                      solr_prompt: 'status',
                      display_name: 'Status',
                      options: applicantStatuses.map(g => [titleize(g.name), g.id])
                    }
                  ])}
                  selected={filters}
                  submitFilters={setFilters}
                  itemsName='Schools'
                />
              </div>
              <h3 className='cross-app-total-count'>
                {`${currentTotal} ${pluralize(currentTotal, 'applicant has', 'applicants have')} applied to other schools`}
              </h3>
              <ContactList
                className='cross-app-current-table'
                columns={currentSchoolColumns}
                contacts={currentSchools}
                emptyMessage='There are no schools in this list'
              />
            </div>
            <div className='cross-app-historical'>
              <h3>Top schools by number of applicants</h3>
              {schoolHistories.map((school) => <HistoricalSchool school={school} totalHistories={totalHistories} />)}
            </div>
          </div>
        )}
      />
    </React.Fragment>
  );
};

const mapDucksToProps = () => ({
  schoolYears: schoolYearsDuck,
  customer: singleDuck(customersDuck, 'current'),
  applicantStatuses: filterDuck(statusesDuck, {
    stage: ['applicant', 'application_complete', 'accepted', 'enrollment', 'enrolled']
  }),
  fields: dependentDuck(
    currentCustomer,
    ({ value: customer }) => filterDuck(fieldsDuck, { solr_prompt: ['grade_id', ...customer.custom_filters_by_name] })
  )
});

const mapDispatchToProps = (dispatch) => ({
  showModal: (type) => dispatch(modals.actions.show(type))
});

export default compose(
  withRouter,
  withFiltering,
  duckify(mapDucksToProps, () => ({}), mapDispatchToProps),
  withSchoolYear(({ customer }) => customer.current_enrollment_year)
)(CrossApplicantsReport);
